import { FinancingBanner } from "@/components/FinancingBanner";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, Rocket, Eraser } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money, computeTotals, lineItemAmount } from "@/lib/formCompute";
import { getTemplate } from "@/lib/formTemplates";
import Seo from "@/components/Seo";

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#12355B";
    const pos = (e) => {
      const rect = c.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };
    const start = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); };
    const end = () => { if (drawing.current) { drawing.current = false; onChange(c.toDataURL("image/png")); } };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); c.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", end);
    };
  }, [onChange]);
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(""); };
  return (
    <div>
      <canvas ref={canvasRef} width={520} height={160} className="w-full border-2 border-dashed border-slate-300 rounded-lg bg-white touch-none" data-testid="signature-canvas" />
      <button type="button" onClick={clear} className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"><Eraser className="w-3.5 h-3.5" /> Clear</button>
    </div>
  );
}

export default function SignPage() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("loading");
  const [signerName, setSignerName] = useState("");
  const [sig, setSig] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/public/forms/sign/${token}`); setForm(data); setSignerName(data.customer_name || ""); setStatus("ready"); if (data.signed) setDone(true); }
      catch { setStatus("notfound"); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!signerName.trim() || !sig) return;
    setSubmitting(true);
    try { await api.post(`/public/forms/sign/${token}`, { signer_name: signerName, signature_img: sig }); setDone(true); }
    catch { toast.error("Could not submit your signature. Please try again."); setSubmitting(false); }
  };

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-navy" /></div>;
  if (status === "notfound") return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-6">
      <h1 className="text-2xl font-bold text-navy">Document not available</h1>
      <p className="text-slate-500 mt-2">This link may be invalid or expired.</p>
    </div>
  );

  const tpl = getTemplate(form.form_type);
  const data = form.data || {};

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <Seo title={`${form.title} | Aquila Air & Heating`} description="Review and sign your document." path={`/sign/${token}`} noindex />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Rocket className="h-6 w-6 text-launch" /><span className="font-heading font-extrabold text-navy text-lg">Aquila Air &amp; Heating</span>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
          <h1 className="text-center text-2xl font-bold text-slate-900 mb-1 uppercase">{tpl.name}</h1>
          {form.customer_name && <p className="text-center text-slate-500 text-sm mb-6">Customer: {form.customer_name}</p>}

          {tpl.sections.map((section, si) => (
            <div key={si} className="mb-5">
              {section.title && <h2 className="text-base font-bold text-slate-900 border-b border-slate-200 pb-1 mb-2">{section.title}</h2>}
              {section.intro && <p className="text-slate-600 text-sm mb-2 whitespace-pre-line">{section.intro}</p>}
              {section.computed === "invoice" && (
                <div className="text-sm">
                  {(data.line_items || []).map((r, i) => (
                    <div key={i} className="flex justify-between py-0.5 text-slate-600"><span>{r.qty} × {r.description}</span><span>{money(lineItemAmount(r))}</span></div>
                  ))}
                  <div className="flex justify-between border-t border-slate-300 mt-1 pt-1 font-bold text-slate-900"><span>Total</span><span>{money(computeTotals("invoice", data).total)}</span></div>
                </div>
              )}
              {section.computed === "change_total" && (
                <div className="flex justify-between text-sm font-bold text-slate-900"><span>Current Balance Remaining</span><span>{money(computeTotals("change_total", data).currentBalanceRemaining)}</span></div>
              )}
              {section.fields.filter((f) => !["lineitems", "change_items"].includes(f.type)).length > 0 && (
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  {section.fields.filter((f) => !["lineitems", "change_items"].includes(f.type)).map((f) => {
                    const v = data[f.key];
                    if (v === undefined || v === "" || v === false) return null;
                    return <div key={f.key}><span className="text-slate-500">{f.label}: </span><span className="text-slate-800 font-medium">{f.type === "checkbox" ? "Yes" : String(v)}</span></div>;
                  })}
                </div>
              )}
            </div>
          ))}

          <FinancingBanner className="mt-6 pt-6 border-t border-slate-200" compact />

          {done ? (
            <div className="text-center py-6 border-t border-slate-200 mt-4" data-testid="sign-success">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-navy">Signed — thank you!</h2>
              <p className="text-slate-500 mt-1">Your signed copy has been recorded. Aquila Air & Heating will be in touch.</p>
              {data.owner_signature_img && <img src={data.owner_signature_img} alt="Signature" className="h-20 mx-auto mt-4 border-b border-slate-300" />}
            </div>
          ) : (
            <form onSubmit={submit} className="border-t border-slate-200 mt-4 pt-5" data-testid="sign-form">
              <h2 className="text-base font-bold text-slate-900 mb-3">Sign to Approve</h2>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Full Name</label>
              <input value={signerName} onChange={(e) => setSignerName(e.target.value)} required className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mb-4" data-testid="sign-name" />
              <label className="block text-xs font-semibold text-slate-500 mb-1">Draw your signature</label>
              <SignaturePad onChange={setSig} />
              <button type="submit" disabled={submitting || !sig || !signerName.trim()} className="mt-5 w-full bg-launch text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90" data-testid="sign-submit">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign & Approve"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
